import React from 'react';
import { Clock, MapPin, Phone, Mail } from 'lucide-react';

interface OperatingHour {
  day: string;
  openTime: string;
  closeTime: string;
  isClosed: boolean;
}

interface Gym {
  _id: string;
  name: string;
  code: string;
  phone: string;
  address: string;
  city: string;
  photoUrl: string;
  timezone: string;
  description?: string;
  facilities?: string[];
  operatingHours?: OperatingHour[];
  coverPhoto?: string;
}

interface OverviewTabProps {
  gym: Gym;
}

const OverviewTab: React.FC<OverviewTabProps> = ({ gym }) => {
  return (
    <div className="space-y-8">
      {/* General Information */}
      <div>
        <h3 className="text-lg font-semibold text-white mb-4">General Information</h3>
        <div className="grid grid-cols-2 gap-6">
          <div className="flex items-start gap-3">
            <MapPin className="text-[#23F47C] mt-1 flex-shrink-0" size={20} />
            <div>
              <p className="text-gray-400 text-sm">Address</p>
              <p className="text-white font-medium">{gym.address}</p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <Phone className="text-[#23F47C] mt-1 flex-shrink-0" size={20} />
            <div>
              <p className="text-gray-400 text-sm">Phone</p>
              <p className="text-white font-medium">{gym.phone}</p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <Mail className="text-[#23F47C] mt-1 flex-shrink-0" size={20} />
            <div>
              <p className="text-gray-400 text-sm">City</p>
              <p className="text-white font-medium">{gym.city}</p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <Clock className="text-[#23F47C] mt-1 flex-shrink-0" size={20} />
            <div>
              <p className="text-gray-400 text-sm">Timezone</p>
              <p className="text-white font-medium">{gym.timezone}</p>
            </div>
          </div>
        </div>
      </div>

      {/* About Gym */}
      {(gym.description || gym.facilities?.length) && (
        <div>
          <h3 className="text-lg font-semibold text-white mb-4">About Gym</h3>

          {gym.description && (
            <div className="mb-6">
              <p className="text-gray-400 text-sm mb-2">Description</p>
              <p className="text-white">{gym.description}</p>
            </div>
          )}

          {gym.facilities && gym.facilities.length > 0 && (
            <div>
              <p className="text-gray-400 text-sm mb-3">Facilities</p>
              <div className="flex flex-wrap gap-2">
                {gym.facilities.map((facility, index) => (
                  <span
                    key={index}
                    className="bg-[#23F47C] bg-opacity-10 text-[#23F47C] px-4 py-2 rounded-full text-sm font-medium"
                  >
                    {facility}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Operating Hours */}
      {gym.operatingHours && gym.operatingHours.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold text-white mb-4">Operating Hours</h3>
          <div className="space-y-2">
            {gym.operatingHours.map((hour, index) => (
              <div
                key={index}
                className="flex justify-between items-center p-3 bg-gray-800 rounded"
              >
                <span className="text-white font-medium">{hour.day}</span>
                <span className="text-gray-400">
                  {hour.isClosed ? (
                    <span className="text-red-400">Closed</span>
                  ) : (
                    `${hour.openTime} - ${hour.closeTime}`
                  )}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default OverviewTab;
